/**
 * Sticky ATC bar (section__sticky-atc): scroll reveal, variant sync, PDP sync or cart drawer.
 */
(function () {
  "use strict";

  var globalSyncBound = false;
  var DESKTOP_MIN_WIDTH = 1024;

  function headerOffset() {
    var raw = getComputedStyle(document.documentElement).getPropertyValue("--header-height");
    var n = parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  }

  function formatMoney(cents) {
    if (window.theme && window.theme.utils && typeof window.theme.utils.formatMoney === "function") {
      return window.theme.utils.formatMoney(cents);
    }
    return "$" + (Number(cents) / 100).toFixed(2);
  }

  function parseProduct(jsonEl) {
    if (!jsonEl) return null;
    try {
      return JSON.parse(jsonEl.textContent || "{}");
    } catch (_e) {
      return null;
    }
  }

  function getProductFromOptions(productOptions) {
    if (!productOptions || !productOptions.dataset.product) return null;
    return parseProduct(document.getElementById(productOptions.dataset.product));
  }

  function getVariantFromProduct(product, variantId) {
    if (!product || !Array.isArray(product.variants) || !variantId) return null;
    return product.variants.find(function (v) {
      return String(v.id) === String(variantId);
    });
  }

  function stickyMatchesSource(root, productOptions, product) {
    if (!root || !productOptions) return false;

    var rootHandle = root.getAttribute("data-product-handle");
    var sourceHandle = productOptions.getAttribute("data-product-handle");
    if (rootHandle && sourceHandle) return rootHandle === sourceHandle;

    var rootId = root.getAttribute("data-product-id");
    return !!(product && rootId && String(product.id) === String(rootId));
  }

  function resolveVariantId(productOptions, input) {
    if (input && input.getAttribute("data-variant-id")) {
      return input.getAttribute("data-variant-id");
    }
    if (productOptions && productOptions.dataset.masterSelectId) {
      var select = document.getElementById(productOptions.dataset.masterSelectId);
      if (select && select.value) return select.value;
    }
    var sourceRoot = productOptions && productOptions.closest("s-product");
    if (sourceRoot) {
      var idField = sourceRoot.querySelector('select[name="id"], input[name="id"]');
      if (idField && idField.value) return idField.value;
    }
    return null;
  }

  function getVariantPriceMap(sourceRoot) {
    if (!sourceRoot) return {};
    var mapEl =
      sourceRoot.querySelector("[data-variant-price-map]") ||
      sourceRoot.querySelector("#ProductVariantPriceMap");
    if (!mapEl) return {};
    try {
      return JSON.parse(mapEl.textContent || "{}");
    } catch (_e) {
      return {};
    }
  }

  function getVariantFeaturedImage(variant, product) {
    if (!variant) return null;
    if (variant.featured_image && variant.featured_image.src) return variant.featured_image;

    if (variant.featured_media && product && Array.isArray(product.media)) {
      var media = product.media.find(function (m) {
        return String(m.id) === String(variant.featured_media.id);
      });
      if (media && media.preview_image && media.preview_image.src) return media.preview_image;
      if (media && media.src) return media;
    }

    if (product && product.featured_image && product.featured_image.src) return product.featured_image;
    return null;
  }

  function updateStickyPrice(root, variant, sourceRoot) {
    var priceTargets = root.querySelectorAll("[js-price]");
    if (!priceTargets.length) return;

    var priceSrc = sourceRoot && sourceRoot.querySelector("[js-price]");
    if (priceSrc) {
      priceTargets.forEach(function (priceDst) {
        priceDst.innerHTML = priceSrc.innerHTML;
      });
      return;
    }

    if (!variant) return;
    var priceMap = getVariantPriceMap(sourceRoot);
    var row = priceMap[String(variant.id)] || {};
    var price = typeof row.price === "number" ? row.price : variant.price;
    var compareAt =
      typeof row.compare_at_price === "number" ? row.compare_at_price : variant.compare_at_price;
    var onSale = compareAt > price;

    priceTargets.forEach(function (priceDst) {
      var regular = priceDst.querySelector(".price__regular");
      var compare = priceDst.querySelector(".price__compare");
      if (regular) {
        regular.textContent = formatMoney(price);
        regular.classList.toggle("inline", onSale);
        regular.classList.toggle("text-red", onSale);
      }
      if (compare) {
        if (onSale) {
          compare.textContent = formatMoney(compareAt);
          compare.style.display = "";
        } else {
          compare.style.display = "none";
        }
      }
    });
  }

  function updateStickyColorSwatch(root, sourceRoot) {
    var swatch = root.querySelector("[js-sticky-color-swatch]");
    if (!swatch || !sourceRoot) return;

    var activeSwatch = sourceRoot.querySelector(
      ".product__swatch.active .product__swatch-inner, .product__swatch-input:checked + label .product__swatch-inner",
    );
    if (!activeSwatch) {
      activeSwatch = sourceRoot.querySelector(".product__swatch-inner");
    }
    if (!activeSwatch) return;

    swatch.style.backgroundImage = activeSwatch.style.backgroundImage || "";
    swatch.style.backgroundColor = activeSwatch.style.backgroundColor || "";
    swatch.style.backgroundSize = "cover";
    swatch.style.backgroundPosition = "center";
  }

  function updateStickyImage(root, variant, sourceRoot, product) {
    var stickyImg = root.querySelector("[js-sticky-image]");
    if (!stickyImg || !variant) return;

    if (sourceRoot) {
      var tmpl = sourceRoot.querySelector('template[data-variant-sticky-image="' + variant.id + '"]');
      if (tmpl && tmpl.content && tmpl.content.firstElementChild) {
        stickyImg.innerHTML = tmpl.content.firstElementChild.outerHTML;
        return;
      }
    }

    var image = getVariantFeaturedImage(variant, product);
    if (!image || !image.src) return;

    var alt = image.alt
      ? String(
        theme.utils && theme.utils.stripProductMediaColorTag
          ? theme.utils.stripProductMediaColorTag(image.alt)
          : image.alt,
      ).replace(/"/g, "&quot;")
      : "";
    stickyImg.innerHTML =
      '<picture class="w-full h-full"><img src="' +
      image.src +
      '" alt="' +
      alt +
      '" class="w-full h-full object-cover" loading="lazy"></picture>';
  }

  function updateStickyLabels(root, variant, sourceRoot) {
    var colorText = "";
    if (sourceRoot) {
      var colorSrc = sourceRoot.querySelector("[js-color-label]");
      if (colorSrc) colorText = colorSrc.textContent.trim();
    }
    if (!colorText && variant && variant.option1) colorText = variant.option1;

    root.querySelectorAll("[js-color-label-custom]").forEach(function (el) {
      if (colorText) el.textContent = colorText;
    });
  }

  function setStickyAtcLoading(stickyBtn, loading) {
    if (!stickyBtn) return;
    var spinner = stickyBtn.querySelector("loading-spinner");
    if (loading) {
      stickyBtn.setAttribute("disabled", "");
      if (spinner) spinner.setAttribute("loading", "");
      return;
    }
    if (spinner) spinner.removeAttribute("loading");
  }

  function clearStickyAtcLoading(stickyBtn, mainAtc) {
    if (!stickyBtn) return;
    setStickyAtcLoading(stickyBtn, false);
    if (mainAtc && mainAtc.disabled) stickyBtn.setAttribute("disabled", "");
    else stickyBtn.removeAttribute("disabled");
  }

  function watchMainAtcForLoadingEnd(stickyBtn, mainAtc) {
    if (!stickyBtn || !mainAtc) return;

    var mainSpinner = mainAtc.querySelector("loading-spinner");
    if (!mainSpinner) {
      clearStickyAtcLoading(stickyBtn, mainAtc);
      return;
    }

    var cleaned = false;
    var sawMainLoading = mainSpinner.hasAttribute("loading");

    function cleanup() {
      if (cleaned) return;
      cleaned = true;
      clearStickyAtcLoading(stickyBtn, mainAtc);
      observer.disconnect();
      clearTimeout(noLoadingTimer);
      clearTimeout(fallbackTimer);
    }

    var observer = new MutationObserver(function () {
      if (mainSpinner.hasAttribute("loading")) sawMainLoading = true;
      if (sawMainLoading && !mainSpinner.hasAttribute("loading")) cleanup();
    });
    observer.observe(mainSpinner, { attributes: true, attributeFilter: ["loading"] });

    var noLoadingTimer = setTimeout(function () {
      if (
        !sawMainLoading &&
        !mainSpinner.hasAttribute("loading") &&
        !mainAtc.hasAttribute("disabled")
      ) {
        cleanup();
      }
    }, 300);

    var fallbackTimer = setTimeout(cleanup, 15000);
  }

  function updateStickyButton(root, variant, sourceRoot) {
    var stickyBtn = root.querySelector("[js-sticky-atc]");
    if (!stickyBtn || !variant) return;

    var mainAtc = sourceRoot && sourceRoot.querySelector("[js-main-add-to-cart]");
    if (mainAtc) {
      if (mainAtc.disabled) stickyBtn.setAttribute("disabled", "");
      else stickyBtn.removeAttribute("disabled");
      if (mainAtc.dataset.variantId) stickyBtn.dataset.variantId = mainAtc.dataset.variantId;
      if (mainAtc.dataset.variantAvailable) {
        stickyBtn.dataset.variantAvailable = mainAtc.dataset.variantAvailable;
      }
      if (mainAtc.dataset.variantOnSale) stickyBtn.dataset.variantOnSale = mainAtc.dataset.variantOnSale;
      var mainText = mainAtc.querySelector("[js-atc-text], [data-atc-text]");
      var stickyText = stickyBtn.querySelector("[js-atc-text], [data-atc-text]");
      if (mainText && stickyText) stickyText.textContent = mainText.textContent;
      return;
    }

    if (variant.available) stickyBtn.removeAttribute("disabled");
    else stickyBtn.setAttribute("disabled", "");

    var stickyText = stickyBtn.querySelector("[js-atc-text], [data-atc-text]");
    if (stickyText) {
      stickyText.textContent = variant.available ? "Add to Cart" : "Sold Out";
    }
  }

  function applyVariantToSticky(root, variant, sourceRoot, product) {
    if (!variant) return;

    var idInput = root.querySelector('form [name="id"]');
    if (idInput) idInput.value = String(variant.id);

    updateStickyLabels(root, variant, sourceRoot);
    updateStickyPrice(root, variant, sourceRoot);
    updateStickyImage(root, variant, sourceRoot, product);
    updateStickyColorSwatch(root, sourceRoot);
    updateStickyButton(root, variant, sourceRoot);
  }

  function syncStickyFromSource(root, productOptions, input, eventVariant) {
    if (!stickyMatchesSource(root, productOptions, getProductFromOptions(productOptions))) return;

    var sourceRoot = productOptions.closest("s-product");
    var product = getProductFromOptions(productOptions);
    var variant =
      eventVariant ||
      getVariantFromProduct(product, resolveVariantId(productOptions, input)) ||
      null;

    if (!variant) return;
    applyVariantToSticky(root, variant, sourceRoot, product);
  }

  function syncAllStickiesFromOptions(productOptions, input, eventVariant) {
    if (!productOptions) return;
    document.querySelectorAll("[js-sticky-atc-root]").forEach(function (root) {
      syncStickyFromSource(root, productOptions, input, eventVariant);
    });
  }

  function bindGlobalVariantSync() {
    if (globalSyncBound) return;
    globalSyncBound = true;

    document.addEventListener(
      "change",
      function (event) {
        var input = event.target;
        if (!input || !input.matches("[js-product-option-selector]")) return;
        var productOptions = input.closest("product-options");
        if (!productOptions) return;

        window.setTimeout(function () {
          syncAllStickiesFromOptions(productOptions, input, null);
        }, 0);
      },
      true,
    );

    document.addEventListener("variant:change", function (event) {
      var variant = event.detail && event.detail.variant;
      var productOptions =
        (event.target && event.target.closest && event.target.closest("product-options")) ||
        null;

      if (!productOptions) {
        var sourceRoot = event.target && event.target.closest && event.target.closest("s-product");
        if (!sourceRoot) return;
        productOptions = sourceRoot.querySelector("product-options");
      }
      if (!productOptions) return;

      syncAllStickiesFromOptions(productOptions, null, variant);
    });
  }

  function findSourceRoot(root) {
    var productId = root.getAttribute("data-product-id");
    if (productId) {
      var nodes = document.querySelectorAll("s-product[data-product-id]");
      for (var i = 0; i < nodes.length; i++) {
        if (String(nodes[i].getAttribute("data-product-id")) === String(productId)) {
          return nodes[i];
        }
      }
    }

    var syncSelector = root.getAttribute("data-sync-source");
    if (syncSelector) {
      var el = document.querySelector(syncSelector);
      if (el) return el.closest("s-product") || el;
    }

    return null;
  }

  function getMainAtc(root) {
    var sourceRoot = findSourceRoot(root);
    if (sourceRoot) {
      var inSource = sourceRoot.querySelector("[js-main-add-to-cart]");
      if (inSource) return inSource;
    }
    return document.querySelector("s-product [js-main-add-to-cart]");
  }

  function getRevealAnchor(root, sourceRoot, behavior) {
    var custom = root.getAttribute("data-scroll-anchor");
    if (custom) {
      var el = document.querySelector(custom);
      if (el) return el;
    }

    if (
      behavior === "sync_pdp" &&
      window.innerWidth < DESKTOP_MIN_WIDTH &&
      sourceRoot
    ) {
      return (
        sourceRoot.querySelector(".product__title") ||
        sourceRoot.querySelector("[id^='ProductInfo-']") ||
        getMainAtc(root)
      );
    }

    return getMainAtc(root);
  }

  function getRevealScrollMode(root, behavior) {
    if (root.getAttribute("data-scroll-anchor")) {
      return "custom";
    }
    if (behavior === "sync_pdp" && window.innerWidth < DESKTOP_MIN_WIDTH) {
      return "mobile_pdp";
    }
    return "default";
  }

  const chat = {
    host: null,
    bottom: "",
    bubbles: null,
    sticky: null,
    show: false,
    timer: 0,
    observer: null,
  };

  const bubbleRow = () => {
    const close = document.querySelector('iframe[title="Close message"]');
    const message = document.querySelector('iframe[title="Message from company"]');
    return close?.parentElement && close.parentElement === message?.parentElement ? close.parentElement : null;
  };

  const launcherHost = () => {
    if (chat.host) return chat.host;
    const frame =
      document.getElementById("launcher") ||
      document.querySelector('iframe[title="Button to launch messaging window"]');
    if (!frame) return null;
    let node = frame;
    while (node && node !== document.documentElement && getComputedStyle(node).position !== "fixed") {
      node = node.parentElement;
    }
    chat.host = node || frame;
    chat.bottom = chat.host.style.bottom || getComputedStyle(chat.host).bottom || "";
    return chat.host;
  };

  const setBubblesHidden = (hidden) => {
    const row = bubbleRow() || chat.bubbles;
    if (!row) return;
    chat.bubbles = row;
    if (hidden) {
      row.style.setProperty("display", "none", "important");
      row.style.setProperty("visibility", "hidden", "important");
      row.style.setProperty("pointer-events", "none", "important");
      return;
    }
    ["display", "visibility", "pointer-events"].forEach((prop) => row.style.removeProperty(prop));
    chat.bubbles = null;
  };

  const resetChat = () => {
    const host = chat.host;
    if (host) {
      host.style.removeProperty("bottom");
      host.style.removeProperty("transition");
      if (chat.bottom) host.style.bottom = chat.bottom;
    }
    setBubblesHidden(false);
    chat.host = null;
    chat.bottom = "";
    chat.sticky = null;
    chat.show = false;
  };

  const ensureChatObserver = () => {
    if (chat.observer) return;
    chat.observer = new MutationObserver(() => {
      if (!chat.sticky) return;
      clearTimeout(chat.timer);
      chat.timer = setTimeout(() => syncChat(chat.sticky, chat.show, "sync_pdp", true), 50);
    });
    chat.observer.observe(document.body, { childList: true, subtree: true });
  };

  const syncChat = (stickyEl, show, behavior, adjustChat) => {
    if (!adjustChat) {
      resetChat();
      return;
    }

    const mobilePdp = behavior === "sync_pdp" && window.innerWidth < DESKTOP_MIN_WIDTH;
    if (!mobilePdp) {
      resetChat();
      return;
    }

    ensureChatObserver();
    chat.sticky = stickyEl;
    chat.show = show;
    setBubblesHidden(true);
    const host = launcherHost();
    if (!host) return;
    host.style.removeProperty("bottom");
    host.style.removeProperty("transition");
    if (show && stickyEl) {
      host.style.setProperty("bottom", `${stickyEl.offsetHeight + 12}px`, "important");
      host.style.setProperty("transition", "bottom 0.3s ease", "important");
    } else if (chat.bottom) {
      host.style.bottom = chat.bottom;
    }
  };

  function isInViewport(el, header) {
    if (!el) return false;
    var rect = el.getBoundingClientRect();
    if (rect.width <= 0 && rect.height <= 0) return false;
    return rect.top < window.innerHeight && rect.bottom > header;
  }

  function shouldShowSticky(revealAnchor, mainAtc, revealMode) {
    var header = headerOffset();

    if (revealMode === "mobile_pdp") {
      if (!mainAtc) return false;
      return !isInViewport(mainAtc, header);
    }

    if (!revealAnchor) return false;

    var pastReveal = revealAnchor.getBoundingClientRect().top < header;

    if (revealMode === "default") {
      return pastReveal;
    }

    return pastReveal && !isInViewport(mainAtc, header);
  }

  function isDesktopBelowHeader(root) {
    return (
      root.getAttribute("data-desktop-below-header") === "true" &&
      window.innerWidth >= DESKTOP_MIN_WIDTH
    );
  }

  function initRoot(root) {
    var sticky = root.querySelector("[js-sticky]");
    if (!sticky) return;

    var footer = document.querySelector("#shopify-section-footer");
    var behavior = root.getAttribute("data-behavior");
    var stickyBtn = root.querySelector("[js-sticky-atc]");
    var sourceRoot = findSourceRoot(root);
    var mainAtc = getMainAtc(root);

    function onScroll() {
      var revealMode = getRevealScrollMode(root, behavior);
      var revealAnchor = getRevealAnchor(root, sourceRoot, behavior);

      if (revealMode !== "mobile_pdp" && !revealAnchor) {
        sticky.classList.remove("show");
        if (footer) footer.style.paddingBottom = "0px";
        return;
      }
      var show = shouldShowSticky(revealAnchor, mainAtc, revealMode);
      sticky.classList.toggle("show", show);
      if (footer && !isDesktopBelowHeader(root)) {
        footer.style.paddingBottom = show ? sticky.offsetHeight + "px" : "0px";
      } else if (footer) {
        footer.style.paddingBottom = "0px";
      }

      syncChat(sticky, show, behavior, root.getAttribute("data-adjust-chat") === "true");
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    onScroll();

    if (sourceRoot) {
      var productOptions = sourceRoot.querySelector("product-options");
      if (productOptions) {
        syncAllStickiesFromOptions(productOptions, null, null);
      }
    }

    if (behavior === "sync_pdp" && stickyBtn && mainAtc) {
      stickyBtn.addEventListener("click", function (e) {
        e.preventDefault();
        if (stickyBtn.getAttribute("data-choose-size") === "true") {
          window.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }
        if (stickyBtn.disabled || mainAtc.disabled) return;
        setStickyAtcLoading(stickyBtn, true);
        mainAtc.click();
        watchMainAtcForLoadingEnd(stickyBtn, mainAtc);
      });
    }
  }

  function init() {
    bindGlobalVariantSync();
    document.querySelectorAll("[js-sticky-atc-root]").forEach(initRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
})();
